import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import Button from 'react-bootstrap/Button';
import ReactGA from 'react-ga4';
import { Contract, ContractFactory, isAddress, zeroPadValue } from 'ethers';

import LoadingButton from '../Widgets/LoadingButton';
import ToastMessage from '../Widgets/ToastMessage';
import commonContextsWrapper from '../Helpers/commonContextsWrapper';
import { EVM_BLOCKCHAINS, TELOS_EVM_CHAIN, getEVMChainSlug } from '../config';

import OFTAdapterBinaryURL from '../Assets/OFTAdapter.bin';
import OFTAdapterABIURL from '../Assets/OFTAdapter.abi';
import OFTTokenBinaryURL from '../Assets/OFTToken.bin';
import OFTTokenABIURL from '../Assets/OFTToken.abi';

import './SetUpBridgeModal.css';

class SetUpBridgeModal extends React.Component {
    get #BRIDGE_STORAGE_KEY() {
        return `${this.evmSession.address}-tEVMBridge`;
    };

    get sharedState() {
        return this.props.sharedState?.[0];
    }

    get evmSession() {
        return this.sharedState?.evmSession;
    }

    get token() {
        return this.sharedState?.token;
    }

    get sourceChain() {
        return TELOS_EVM_CHAIN;
    };

    get sourceChainBridgeAddressSaved() {
        return localStorage.getItem(this.#BRIDGE_STORAGE_KEY);
    };

    constructor(props) {
        super(props);

        this.state = {
            isTransacting: false,
            isRunningOnDestChain: false,
            isBridgeActive: false,
            errorMessage: undefined,
            multipleBridgesWarningAcquainted: false,
            sourceChainBridgeAddress: undefined,
            selectedDestinationChainID: undefined,
            destinationChainBridgeAddress: undefined
        };

        this.BodyContentTypes = {
            WARNING: 0,
            SET_TEVM_BRIDGE: 1,
            SELECT_DEST_CHAIN: 2,
            ACTIVATE_BRIDGE: 3,
            BRIDGE_ACTIVE: 4
        };

        this.deployTelosBridge = this.deployTelosBridge.bind(this);
        this.deployDestinationBridge = this.deployDestinationBridge.bind(this);
        this.activateBridge = this.activateBridge.bind(this);
        this.hideErrorMessage = this.hideErrorMessage.bind(this);
        this.close = this.close.bind(this);
    }

    // Actions
    async deployTelosBridge() {
        this.setState({isTransacting: true});

        try {
            const lzEndpoint = this.lzEndpointForChainID(this.evmSession.chainID);
            const contractByteCode = await (await fetch(OFTAdapterBinaryURL)).text();
            const contractABI = await (await fetch(OFTAdapterABIURL)).text();
            const signer = await this.evmSession.provider.getSigner();
            const factory = new ContractFactory(contractABI, contractByteCode, signer);
            const contract = await factory.deploy(this.token.contractAddress, lzEndpoint.address, this.evmSession.address);

            await contract.waitForDeployment();
            
            ReactGA.event('tlos_bridge_deployed', {
                'token_contract': this.token.contractAddress,
                'bridge_contract': contract.target
            });

            this.setState({
                isTransacting: false,
                sourceChainBridgeAddress: contract.target
            });

            this.rememberSourceChainBridge(contract.target);
        }
        catch (error) {
            this.setState({isTransacting: false});
            this.showErrorMessage(error);
        }
    };

    async deployDestinationBridge() {
        this.setState({isTransacting: true});

        try {
            if (this.evmSession.chainID !== this.state.selectedDestinationChainID) {
                await this.evmSession.switchChain(this.state.selectedDestinationChainID);
                this.setState({isRunningOnDestChain: true});
            }

            const lzEndpoint = this.lzEndpointForChainID(this.state.selectedDestinationChainID);
            const peerLzEndpoint = this.lzEndpointForChainID(this.sourceChain.chainID);
            const contractByteCode = await (await fetch(OFTTokenBinaryURL)).text();
            const contractABI = await (await fetch(OFTTokenABIURL)).text();
            const signer = await this.evmSession.provider.getSigner();
            const factory = new ContractFactory(contractABI, contractByteCode, signer);

            const contract = await factory.deploy(
                this.token.name,
                this.token.symbol,
                this.token.precision,
                lzEndpoint.address,
                this.evmSession.address,
                peerLzEndpoint.id,
                zeroPadValue(this.state.sourceChainBridgeAddress, 32)
            );

            await contract.waitForDeployment();

            this.setState({
                isTransacting: false,
                destinationChainBridgeAddress: contract.target
            });
        }
        catch (error) {
            this.setState({isTransacting: false});
            this.showErrorMessage(error);
        }
    };

    async activateBridge() {
        this.setState({isTransacting: true});

        try {
            if (this.evmSession.chainID !== this.sourceChain.chainID) {
                await this.evmSession.switchChain(this.sourceChain.chainID);
                this.setState({isRunningOnDestChain: false});
            }

            const contractABI = await (await fetch(OFTAdapterABIURL)).text();
            const peerLzEndpoint = this.lzEndpointForChainID(this.state.selectedDestinationChainID);
            const signer = await this.evmSession.provider.getSigner();
            const oftAdapterContract = new Contract(this.state.sourceChainBridgeAddress, contractABI, signer);

            await oftAdapterContract.setPeer(
                peerLzEndpoint.id,
                zeroPadValue(this.state.destinationChainBridgeAddress, 32)
            );

            this.setState({isBridgeActive: true, isTransacting: false});
        }
        catch (error) {
            this.setState({isTransacting: false});
            this.showErrorMessage(error);
        }
    };

    // Components
    modalBodyContent() {
        const ContentType = this.BodyContentTypes;
        const currentType = this.modalBodyContentType();

        switch (currentType) {
            case ContentType.WARNING:
                return (
                    <Stack>
                        <p style={{textAlign: 'center'}}>
                            Caution! Make sure to avoid setting up multiple functioning bridges.
                        </p>
                        
                        { !this.state.sourceChainBridgeAddress &&
                            <span style={{textAlign: 'center', color: 'var(--blue-text)', cursor: 'pointer'}} onClick={() => this.setSourceBridgeAddress()}>
                                Already have a bridge on Telos?
                            </span>
                        }
                    </Stack>
                );
            case ContentType.SET_TEVM_BRIDGE:
                return (
                    <p style={{textAlign: 'center', marginBottom: 0}}>
                        Deploy a bridging contract on Telos EVM.
                    </p>
                );
            case ContentType.SELECT_DEST_CHAIN:
                return (
                    <Stack>
                        <p>Select a chain to bridge to:</p>
                        <Form.Select defaultValue={0} disabled={this.state.isTransacting} onChange={e => { this.setState({selectedDestinationChainID: Number(e.target.value)})}}>
                            <option hidden value={0}>-- select chain --</option>
                            { this.supportedDestinationChains().map(c => <option value={c.chainID} key={c.chainID}>{c.name}</option>) }
                        </Form.Select>
                    </Stack>
                );
            case ContentType.ACTIVATE_BRIDGE:
                return (
                    <p style={{textAlign: 'center', marginBottom: 0}}>
                        You can now activate the bridge.
                    </p>
                );
            case ContentType.BRIDGE_ACTIVE:
                const destinationChain = this.getChainByID(this.state.selectedDestinationChainID);
                const sourceChain = this.sourceChain;

                return (
                    <Stack style={{gap: '1rem'}}>
                        <p style={{textAlign: 'center', marginBottom: 0}}>
                            The bridge to <b>{destinationChain.name}</b> has been successfully set up and is now operating. Make sure to note the bridges' addresses in case you haven't.
                        </p>

                        <Stack direction="vertical" style={{gap: '0.5rem', overflowWrap: 'break-word'}}>
                            <div style={{fontWeight: 400}}>{sourceChain.name}: <span className="evmAddressHighlighted">{this.state.sourceChainBridgeAddress}</span></div>
                            <div style={{fontWeight: 400}}>{destinationChain.name}: <span className="evmAddressHighlighted">{this.state.destinationChainBridgeAddress}</span></div>
                        </Stack>
                    </Stack>
                );
            default:
                return null;
        }
    };

    actionButton() {
        const ContentType = this.BodyContentTypes;
        const currentType = this.modalBodyContentType();
        const isLoading = this.state.isTransacting;

        switch (currentType) {
            case ContentType.WARNING:
                return <Button variant="primary" onClick={() => this.setState({multipleBridgesWarningAcquainted: true})}>GOT IT</Button>;
            case ContentType.SET_TEVM_BRIDGE:
                return <LoadingButton variant="primary" onClick={this.deployTelosBridge} isLoading={isLoading}>DEPLOY</LoadingButton>;
            case ContentType.SELECT_DEST_CHAIN:
                return <LoadingButton variant="primary" onClick={this.deployDestinationBridge} isLoading={isLoading} disabled={!this.state.selectedDestinationChainID}>DEPLOY</LoadingButton>; 
            case ContentType.ACTIVATE_BRIDGE:
                return <LoadingButton variant="primary" onClick={this.activateBridge} isLoading={isLoading}>ACTIVATE</LoadingButton>;
            case ContentType.BRIDGE_ACTIVE:
                return <Button variant="primary" onClick={this.close}>OK</Button>;
            default:
                return null;
        }
    };

    // Error Message Toast
    showErrorMessage(error) {
        if (error.code === 'ACTION_REJECTED') {
            return;
        }

        const internalError = error.info?.error;
        const errorMessage = (internalError?.data?.message || internalError?.message || error.message);

        this.setState({errorMessage: errorMessage});
    };

    hideErrorMessage() {
        this.setState({errorMessage: undefined});
    };

    // LocalStorage
    rememberSourceChainBridge(address) {
        localStorage.setItem(this.#BRIDGE_STORAGE_KEY, address);
    };

    // Misc
    modalBodyContentType() {
        const state = this.state;

        if (!state.multipleBridgesWarningAcquainted) {
            return this.BodyContentTypes.WARNING;
        }

        if (!state.sourceChainBridgeAddress) {
            return this.BodyContentTypes.SET_TEVM_BRIDGE;
        }

        if (!state.selectedDestinationChainID || !this.state.destinationChainBridgeAddress) {
            return this.BodyContentTypes.SELECT_DEST_CHAIN;
        }

        if (!this.state.isBridgeActive) {
            return this.BodyContentTypes.ACTIVATE_BRIDGE;
        }
        
        return this.BodyContentTypes.BRIDGE_ACTIVE;
    };

    supportedDestinationChains() {
        return Object.values(EVM_BLOCKCHAINS).filter(c => (c.chainID !== this.sourceChain.chainID && !!c.lzEndpoint));
    };

    subtitleText() {
        const ContentType = this.BodyContentTypes;
        const currentType = this.modalBodyContentType();

        switch (currentType) {
            case ContentType.SET_TEVM_BRIDGE:
                return 'Deploy tEVM bridge (1/3)'
            case ContentType.SELECT_DEST_CHAIN:
                return 'Deploy a contract on the destination chain (2/3)'
            case ContentType.ACTIVATE_BRIDGE:
                return 'Activate the bridge (3/3)'
            case ContentType.BRIDGE_ACTIVE:
                return 'Bridge active';
            default:
                return 'Make your token bridgeable to other chains';
        }
    };

    getChainByID(chainID) {
        const slug = getEVMChainSlug(chainID);
        return EVM_BLOCKCHAINS[slug];
    };

    lzEndpointForChainID(chainID) {
        return this.getChainByID(chainID).lzEndpoint;
    };

    setSourceBridgeAddress() {
        const contractAddress = prompt('Enter address of your bridge on Telos EVM');
        
        if (isAddress(contractAddress)) {
            this.setState({sourceChainBridgeAddress: contractAddress});
        }
    };

    shouldDisplayCancel() {
        const ContentType = this.BodyContentTypes;
        const cancellableContentTypes = [ContentType.WARNING, ContentType.SET_TEVM_BRIDGE, ContentType.SELECT_DEST_CHAIN];
        return cancellableContentTypes.includes(this.modalBodyContentType());
    };

    close() {
        this.props.onCloseClicked();
    };
    
    // React
    componentDidUpdate(prevProps) {
        if (prevProps.isShown !== this.props.isShown) {
            if (this.props.isShown) {
                if (this.sourceChainBridgeAddressSaved && !this.state.sourceChainBridgeAddress) {
                    this.setState({sourceChainBridgeAddress: this.sourceChainBridgeAddressSaved});
                }
            }
            else {
                setTimeout(() => {
                    this.setState({
                        isBridgeActive: false,
                        multipleBridgesWarningAcquainted: false,
                        isRunningOnDestChain: false,
                        sourceChainBridgeAddress: undefined,
                        selectedDestinationChainID: undefined,
                        destinationChainBridgeAddress: undefined
                    });
                }, 800);
            }
        }
    };

    render() {
        return (
            <>
                {/* ToastMessage */}
                <ToastMessage.Error
                    isShown={!!this.state.errorMessage}
                    message={this.state.errorMessage}
                    onAutoClosed={this.hideErrorMessage}
                />

                {/* Main Content */}
                <Modal show={this.props.isShown} onHide={this.close} size="sm" contentClassName="modal-content-dark" backdrop="static" centered>
                    <Modal.Header style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                        <Modal.Title>Set Up Bridge</Modal.Title>
                        <div style={{fontWeight: 300, fontSize: '0.8rem', color: 'var(--white-inactive)'}}>{this.subtitleText()}</div>
                    </Modal.Header>

                    <Modal.Body>{this.modalBodyContent()}</Modal.Body>
                    
                    <Modal.Footer>
                        { this.shouldDisplayCancel() &&
                            <Button variant="secondary" onClick={this.close} disabled={this.state.isTransacting}>Cancel</Button>
                        }

                        {this.actionButton()}
                    </Modal.Footer>
                </Modal>
            </>
        );
    }
}

export default commonContextsWrapper(SetUpBridgeModal);
